import { createContext, useContext, useEffect, useState } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import { CircularProgress } from "@mui/material";

import ErrorPage from "../Components/Others/error-page";

import renderSelectConnectedAccounts from "../Organisms/Modals/Bank Account/select-connected-accounts";
import renderCreditBuilderOptIn from "../Organisms/Modals/Credit-Builder/credit-builder-opt-in";
import renderApplyStakeChecking from "../Organisms/Modals/Stake Checking/apply-stake-checking";
import renderJoinStakeSaver from "../Organisms/Modals/Stake Saver/join-stake-saver";
import renderColoradoRenterRewards from "../Organisms/Modals/Programs/colorado-renter-rewards";
import renderBankStatements from "../Organisms/Modals/Banking/bank-statements";

import { useAppUtilityContext } from "./AppUtilityContext";
import { useModalContext } from "./ModalContext";

import usePrefetchOnLogin from "../CustomHooks/usePrefetchOnLogin";

import {
  clearBankMigrationSkippedStatus,
  getBankAccountMigrationStatusFromDealsResponse,
  getBankMigrationSkippedStatusKey,
  getHasVisitedWelcomeScreenKey,
  hasBankMigrationApplicationStartedFromStakeDebitResponse,
  isBankAccountMigrationNotRequiredByDealsResponse,
  isBankAccountMigrationRequiredByDealsResponse,
  BATTLECARD_FLOW_NAV_STATE,
  getBattlecardRedirectNavState,
  getLegacyCheckRouteFromDealsResponse,
  isLegacyBattlecardRoute,
  isLegacyCheckNotProcessedFromDealsResponse,
  isInstantReleaseCheckPendingFromDealsResponse,
  isLegacyCheckPendingFromDealsResponse,
  isOnboardingModule,
  isUmfUserFromDealsResponse,
  isInstantReleaseUserFromDealsResponse,
  isStakeUtilitiesUserFromDealsResponse,
  isSVDApproved,
  LocalStorage,
  shouldShowLegacyOnlyBattlecardPage,
  shouldShowMigrationAcknowledgementFromStakeDebitResponse,
  isSVDVerificationRejected,
} from "../Utils/helpers";
import { APIQueryKeys, appUtilityKeys } from "../Utils/constants";

const RedirectContext = createContext();
const BANK_MIGRATION_PATHS = [
  "/bank-account-migration",
  "/bank-migration-details",
  "/bank-migration-setting-up",
];
const BANK_MIGRATION_ACKNOWLEDGEMENT_PATH = "/bank-migration-acknowledgement";
const BANK_MIGRATION_STATUS_PATH = "/bank-migration-status";

const isBankMigrationFlowPath = (pathname) =>
  BANK_MIGRATION_PATHS.includes(pathname) ||
  pathname === BANK_MIGRATION_STATUS_PATH ||
  pathname === BANK_MIGRATION_ACKNOWLEDGEMENT_PATH;

export const RedirectContextProvider = () => {
  const [hasVisitedWelcomeScreen, setHasVisitedWelcomeScreen] = useState(
    LocalStorage.read(getHasVisitedWelcomeScreenKey()),
  );

  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const modalContext = useModalContext();

  const { onUserCredentialsUpdate, appUtilityProps, setAppUtilityProps } = useAppUtilityContext();

  const {
    isLoading: isPrefetchingData,
    isSuccess: isDataPrefetchSuccess,
    isError: isDataPrefetchError,
    data: { dealsData, consumerDealsData, appEssentialsData, programsData, stakeDebitData, unclaimedTransactionsData },
    hasUnclaimedTransactionsData,
    shouldShowBattleCardsPage,
    shouldShowInstantReleasePage,
    shouldShowInstantReleaseResumePage,
    shouldShowStakeUtilitiesPage,
    shouldShowStakeUtilitiesResumePage,
    shouldShowLegacyOnlyBattlecardPage,
    shouldShowLegacyOnlyInstantReleasePage,
    errorStatus,
  } = usePrefetchOnLogin({ authKey: appUtilityProps.authKey, hasVisitedWelcomeScreen });

  const onLogin = (data) => {
    queryClient.resetQueries();
    onUserCredentialsUpdate(data);
    clearBankMigrationSkippedStatus();
    setHasVisitedWelcomeScreen(LocalStorage.read(getHasVisitedWelcomeScreenKey()));
  };

  const markVisitedWelcomePage = () => {
    LocalStorage.write(getHasVisitedWelcomeScreenKey(), true);
    setHasVisitedWelcomeScreen(true);
  };

  useEffect(() => {
    const offers = consumerDealsData?.data?.data?.offers ?? [];

    if (isDataPrefetchSuccess) {
      setAppUtilityProps((p) => ({
        ...p,
        hideDealsRoutes: (offers?.length ?? 0) === 0,
        hideCashRoutes: !isSVDApproved(stakeDebitData?.data),
      }));
    }
    //eslint-disable-next-line
  }, [isDataPrefetchSuccess, consumerDealsData, stakeDebitData]);

  useEffect(() => {
    if (!!appUtilityProps.authKey) {
      queryClient.refetchQueries(APIQueryKeys.deals);
      queryClient.refetchQueries(APIQueryKeys.userAccount);
      queryClient.refetchQueries(APIQueryKeys.transferToken);
      if (!hasVisitedWelcomeScreen) {
        queryClient.refetchQueries(APIQueryKeys.mobileAppSummary);
        queryClient.refetchQueries(APIQueryKeys.platformSummaryStats);
      }
    }
  }, [queryClient, appUtilityProps.authKey, hasVisitedWelcomeScreen]);

  useEffect(() => {
    if (
      !appUtilityProps.authKey ||
      isPrefetchingData ||
      !isDataPrefetchSuccess ||
      isOnboardingModule(location.pathname) ||
      location.pathname === "/instant-release" ||
      location.pathname === "/stake-utilities"
    ) {
      return;
    }

    if (shouldShowMigrationAcknowledgementFromStakeDebitResponse(stakeDebitData)) {
      if (location.pathname !== BANK_MIGRATION_ACKNOWLEDGEMENT_PATH) {
        navigate(BANK_MIGRATION_ACKNOWLEDGEMENT_PATH, { replace: true });
      }
      return;
    }

    if (isBankMigrationFlowPath(location.pathname)) {
      return;
    }

    const migrationStatus = getBankAccountMigrationStatusFromDealsResponse(dealsData);
    const hasMigrationToHandle = isBankAccountMigrationRequiredByDealsResponse(dealsData);
    const skippedMigrationStatus = LocalStorage.read(getBankMigrationSkippedStatusKey());

    if (hasBankMigrationApplicationStartedFromStakeDebitResponse(stakeDebitData)) {
      return;
    }

    if (!hasMigrationToHandle) {
      if (location.pathname !== BANK_MIGRATION_STATUS_PATH) {
        LocalStorage.remove(getBankMigrationSkippedStatusKey());
      }
      return;
    }

    if (skippedMigrationStatus === migrationStatus) {
      return;
    }

    navigate("/bank-account-migration", { replace: true, state: { migrationStatus } });
  }, [appUtilityProps.authKey, dealsData, isDataPrefetchSuccess, isPrefetchingData, location.pathname, navigate, stakeDebitData]);

  useEffect(() => {
    if (
      !appUtilityProps.authKey ||
      isPrefetchingData ||
      !isDataPrefetchSuccess ||
      isOnboardingModule(location.pathname) ||
      !BANK_MIGRATION_PATHS.includes(location.pathname)
    ) {
      return;
    }

    if (isBankAccountMigrationNotRequiredByDealsResponse(dealsData)) {
      navigate("/home", { replace: true });
    }
  }, [
    appUtilityProps.authKey,
    dealsData,
    isDataPrefetchSuccess,
    isPrefetchingData,
    location.pathname,
    navigate,
  ]);

  useEffect(() => {
    if (
      !appUtilityProps.authKey ||
      isPrefetchingData ||
      !isDataPrefetchSuccess ||
      location.pathname !== "/unclaimed"
    ) {
      return;
    }

    // Instant release and stake utilities members get their rebate when Stake Checking opens or as a mailed
    // check, so they never claim from this page, whether the application was denied or is still in progress.
    if (
      !isUmfUserFromDealsResponse(dealsData) ||
      isInstantReleaseUserFromDealsResponse(dealsData) ||
      isStakeUtilitiesUserFromDealsResponse(dealsData)
    ) {
      navigate("/home", { replace: true });
    }
  }, [
    appUtilityProps.authKey,
    dealsData,
    isDataPrefetchSuccess,
    isPrefetchingData,
    location.pathname,
    navigate,
  ]);

  useEffect(() => {
    if (
      !appUtilityProps.authKey ||
      isPrefetchingData ||
      !isDataPrefetchSuccess ||
      location.pathname !== "/home"
    ) {
      return;
    }

    if (isLegacyCheckPendingFromDealsResponse(dealsData)) {
      navigate(getLegacyCheckRouteFromDealsResponse(dealsData), { replace: true });
      return;
    }

    if (isInstantReleaseCheckPendingFromDealsResponse(dealsData)) {
      navigate("/instant-release", { replace: true });
      return;
    }

    if (shouldShowInstantReleaseResumePage) {
      navigate("/instant-release", { replace: true });
      return;
    }

    if (shouldShowStakeUtilitiesResumePage) {
      navigate("/stake-utilities", { replace: true });
      return;
    }

    if (shouldShowLegacyOnlyBattlecardPage) {
      navigate("/battlecard", {
        replace: true,
        state: getBattlecardRedirectNavState(dealsData, unclaimedTransactionsData, stakeDebitData),
      });
      return;
    }

    if (shouldShowLegacyOnlyInstantReleasePage) {
      navigate("/instant-release", { replace: true });
    }
  }, [
    appUtilityProps.authKey,
    dealsData,
    isDataPrefetchSuccess,
    isPrefetchingData,
    location.pathname,
    navigate,
    shouldShowInstantReleaseResumePage,
    shouldShowStakeUtilitiesResumePage,
    shouldShowLegacyOnlyBattlecardPage,
    shouldShowLegacyOnlyInstantReleasePage,
    stakeDebitData,
    unclaimedTransactionsData,
  ]);

  useEffect(() => {
    if (
      !appUtilityProps.authKey ||
      isPrefetchingData ||
      !isDataPrefetchSuccess ||
      !isLegacyBattlecardRoute(location.pathname) ||
      location.pathname === "/battlecard/check-not-processed"
    ) {
      return;
    }

    if (isLegacyCheckNotProcessedFromDealsResponse(dealsData)) {
      navigate("/battlecard/check-not-processed", {
        replace: true,
        state: BATTLECARD_FLOW_NAV_STATE,
      });
    }
  }, [
    appUtilityProps.authKey,
    dealsData,
    isDataPrefetchSuccess,
    isPrefetchingData,
    location.pathname,
    navigate,
  ]);

  useEffect(
    () => {
      if (isOnboardingModule(location.pathname) && !isPrefetchingData && isDataPrefetchSuccess) {
        const deepLinkPath = LocalStorage.read(appUtilityKeys.deeplink);

        if (deepLinkPath === "beginConnectBank") {
          renderSelectConnectedAccounts(modalContext, { openPlaidOnMount: true });
        } else if (deepLinkPath === "beginCreditBuilder") {
          renderCreditBuilderOptIn(modalContext);
        } else if (deepLinkPath === "beginSVDApplication") {
          renderApplyStakeChecking(modalContext);
        } else if (deepLinkPath === "joinStakeSaver") {
          renderJoinStakeSaver(modalContext);
        } else if (appEssentialsData?.propertyHasProgram && appEssentialsData?.previewProgramOnLogin) {
          if (programsData?.my_program?.id === "colorado_renter_rewards") {
            renderColoradoRenterRewards(modalContext, {
              occupyFullscreen: true,
            });
          }
        }

        if (deepLinkPath === "beginAddFunds") {
          navigate("/add-cash");
        } else if (deepLinkPath === "battlecardConfirmLegacyAddress") {
          navigate("/battlecard/validate-address", { state: BATTLECARD_FLOW_NAV_STATE });
        } else if (
          deepLinkPath === "battlecardBeginBankApplication" &&
          !isSVDApproved(stakeDebitData?.data) &&
          !isSVDVerificationRejected(stakeDebitData?.data)
        ) {
          navigate("/battlecard/start-shopping", { state: BATTLECARD_FLOW_NAV_STATE });
        } else if (deepLinkPath === "beginConnectDirectDeposit") {
          navigate("/direct-deposit", { state: { openPinWheelOnMount: true } });
        } else if (deepLinkPath === "manageStakePay") {
          navigate("/cash/stakepay-3/manage");
        } else if (deepLinkPath === "payRent") {
          navigate("/cash/stakepay-3");
        } else if (deepLinkPath === "addNeighborhoodCashCard") {
          navigate("/deals/neighborhood-network", { state: { startLinkingCardOnMount: true } });
        } else if (deepLinkPath === "getBankStatements") {
          renderBankStatements(modalContext);
          navigate("/deals");
        } else if (deepLinkPath === "setCardsPin") {
          navigate("/manage-stake-debit");
        } else if (location.pathname === "/welcome" || location.pathname === "/welcome/find-a-home") {
          // Don't redirect away from welcome flow - let it complete naturally
          return;
        } else if (dealsData?.data?.data?.currentDeal?.registration_status === "registered") {
          navigate("/setup-account");
        } else if (hasUnclaimedTransactionsData) {
          navigate("/unclaimed");
        } else if (shouldShowBattleCardsPage) {
          navigate("/battlecard", { state: BATTLECARD_FLOW_NAV_STATE });
        } else if (shouldShowInstantReleasePage) {
          navigate("/instant-release", { replace: true });
        } else if (shouldShowInstantReleaseResumePage) {
          navigate("/instant-release", { replace: true });
        } else if (shouldShowStakeUtilitiesPage) {
          navigate("/stake-utilities", { replace: true });
        } else if (shouldShowStakeUtilitiesResumePage) {
          navigate("/stake-utilities", { replace: true });
        } else if (shouldShowLegacyOnlyBattlecardPage) {
          navigate("/battlecard", {
            state: getBattlecardRedirectNavState(dealsData, unclaimedTransactionsData, stakeDebitData),
          });
        } else if (shouldShowLegacyOnlyInstantReleasePage) {
          navigate("/instant-release", { replace: true });
        } else if (isLegacyCheckPendingFromDealsResponse(dealsData)) {
          navigate(getLegacyCheckRouteFromDealsResponse(dealsData), {
            state: BATTLECARD_FLOW_NAV_STATE,
          });
        } else if (isInstantReleaseCheckPendingFromDealsResponse(dealsData)) {
          navigate("/instant-release", { replace: true });
        } else {
          navigate("/home");
        }

        if (!!deepLinkPath) {
          LocalStorage.remove(appUtilityKeys.deeplink);
        }
      }
    }, //eslint-disable-next-line
    [
      location.pathname,
      isPrefetchingData,
      isDataPrefetchSuccess,
      hasUnclaimedTransactionsData,
      shouldShowBattleCardsPage,
      shouldShowInstantReleasePage,
      shouldShowInstantReleaseResumePage,
      shouldShowStakeUtilitiesPage,
      shouldShowStakeUtilitiesResumePage,
      shouldShowLegacyOnlyBattlecardPage,
      shouldShowLegacyOnlyInstantReleasePage,
      consumerDealsData,
      dealsData,
      appEssentialsData,
      programsData,
      navigate,
    ],
  );

  if (!appUtilityProps.authKey && !isOnboardingModule(location.pathname)) {
    return <Navigate to="/login" />;
  }

  const moduleType = isOnboardingModule(location.pathname) ? "onboarding" : "authenticated";

  // Don't show loading spinner when on /welcome - let the welcome flow continue uninterrupted
  const shouldShowLoading =
    isPrefetchingData && location.pathname !== "/welcome" && location.pathname !== "/welcome/find-a-home";

  return (
    <RedirectContext.Provider value={{ onLogin, markVisitedWelcomePage }}>
      {shouldShowLoading ? (
        <div className={`${moduleType}-page justify-content-center`}>
          <CircularProgress sx={{ color: "var(--mist)" }} />
        </div>
      ) : isDataPrefetchError ? (
        <ErrorPage errorStatus={errorStatus} />
      ) : (
        <Outlet />
      )}
    </RedirectContext.Provider>
  );
};

export const useRedirectContext = () => {
  const modalContextData = useContext(RedirectContext);
  if (modalContextData === undefined) {
    throw new Error("useRedirectContext must be used within a RedirectContextProvider");
  }
  return modalContextData;
};
